import { ReactNode, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Pagination as PaginationUI, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "../ui/pagination";
import { PaginationData } from "@/types/pagination";
import range from 'lodash/range';
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { ColumnType } from "@/types/filter";
import { formatBoolean, formatCpfCnpj, formatCurrency } from "@/utils/format";
import { useDateFormatter } from "@react-aria/i18n";
import { ArrowDown, ArrowUp, ArrowUpDown, Columns3 } from "lucide-react";
import { cn } from "@/utils/cn";
import { Direction, SortState } from "@/types/sort";
import { Skeleton } from "../ui/skeleton";

export type Column = {
  field: string;
  show: boolean;
  label?: string;
  filter?: boolean;
  type?: ColumnType;
  sorted?: boolean;
  getValue?(item: any): ReactNode;
  exportValue?(item: any): string | number | undefined;
  // false: não aparece no cartão da lista no celular
  mobile?: boolean;
}

type DataTableProps = {
  keyExtractor(item: any, index?: number): string | number;
  values?: any[];
  columns: Column[];
  actions?(item: any): React.ReactNode;
  pagination?: PaginationData;
  checkable?: boolean;
  loading?: boolean;
}

export const DataTable = ({values,...props}: DataTableProps) => {
  const [columnSorted, setColumnSorted] = useState<SortState>();
  const [columns, setColumns] = useState(props.columns);
  const formatter = useDateFormatter({dateStyle: "short"});
  const navigate = useNavigate();
  const location = useLocation();

  const extractFieldValue = (field: string, value: any): any => {
    var fields = field.split('.');
    if (value === undefined || value === null) return undefined;
    if (fields.length === 1) return value[field];
    
    var firstElement = fields.shift();

    return extractFieldValue(fields.join('.'),value[firstElement!]);
  }

  const formatValue = (col: Column, row: any): ReactNode => {
    const value = extractFieldValue(col.field, row);
    
    const tipo = col.type||'STRING';
    
    if (tipo === 'CPF_CNPJ'){
      return formatCpfCnpj(value);
    } else if (tipo === 'DATE'){
      return formatter.format(new Date(`${String(value).slice(0, 10)}T00:00:00`));
    } else if (tipo === 'BOOLEAN'){
      return formatBoolean(value);
    } else if (tipo === 'CUSTOM'){
      return col.getValue!(row);
    } else if (tipo === 'CURRENCY'){
      return formatCurrency(value);
    }

    return value;
  }

  const columnSort = (col: Column) => {
    let direction: Direction = ""; //col.field===columnSorted?.name && columnSorted.direction==="asc" ? "desc" : "asc"

    if (col.field===columnSorted?.name && columnSorted.direction==="asc"){
      direction = 'desc';
    } else if (col.field===columnSorted?.name && columnSorted.direction==="desc"){
      direction = '';
    } else {
      direction = 'asc';
    }

    const allParams: string[] =
      ((location && location.search.substr(1).split('&')) || [])
        .filter(param => !param.startsWith('sort'));

    if (direction===''){
      setColumnSorted(undefined)
    } else {
      allParams.push(`sort=${col.label}-${direction}`);
      setColumnSorted({name: col.field, direction: direction})
    }
    navigate({search: allParams.join('&')});    
  }

  const visibleColumns = columns.filter(col => col.show);
  const mobileColumns = visibleColumns.filter(col => col.mobile !== false);
  const colSpan = visibleColumns.length + (props.actions ? 1 : 0) + (props.checkable ? 1 : 0);
  const hasValues = !!values && values.length > 0 && !props.loading;

  const sortIcon = (col: Column) => {
    if (col.field !== columnSorted?.name) return <ArrowUpDown className="h-3.5 w-3.5 opacity-40 group-hover/sort:opacity-100" />;
    return columnSorted.direction === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />;
  }

  const isNumeric = (col: Column) => col.type === 'CURRENCY' || col.type === 'NUMBER';

  const cellValue = (col: Column, row: any): ReactNode => {
    const raw = col.type === 'CUSTOM' ? true : extractFieldValue(col.field, row);
    const value = raw === undefined || raw === null || raw === '' ? undefined : formatValue(col, row);
    return value === undefined || value === null || value === '' || value === false ? <span className="text-muted-foreground/60">—</span> : value;
  }

  const emptyState = (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className="h-32 text-center text-sm text-muted-foreground">
        Nenhum registro encontrado.
      </TableCell>
    </TableRow>
  );

  return (
    <>
    <div className="hidden sm:block">
      <Table>
        <TableHeader className="bg-muted/40">
          <TableRow className="hover:bg-transparent">
            {props.checkable && (
              <TableHead className="w-10 pl-4">
                <Checkbox
                  checked={false}
                  onCheckedChange={(value) => null}
                  aria-label="Selecionar todos"
                />
              </TableHead>
            )}
            {visibleColumns.map((col) => (
              <TableHead key={col.field} className={cn("h-10 px-4 text-xs", isNumeric(col) && "text-right")} aria-sort={col.field === columnSorted?.name ? (columnSorted.direction === 'asc' ? 'ascending' : 'descending') : undefined}>
                {col.sorted ? (
                  <button
                    type="button"
                    onClick={() => columnSort(col)}
                    className={cn("group/sort -mx-1 inline-flex items-center gap-1.5 rounded px-1 py-0.5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", col.field === columnSorted?.name && "text-foreground")}
                  >
                    {col.label}
                    {sortIcon(col)}
                  </button>
                ) : col.label}
              </TableHead>
            ))}
            {props.actions && (
              <TableHead className="h-10 w-px px-4 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" aria-label="Escolher colunas">
                      <Columns3 className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Colunas visíveis</DropdownMenuLabel>
                    {columns.map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.field}
                        checked={column.show}
                        onCheckedChange={(value) =>
                          setColumns(columns.map(p =>
                            p.field === column.field
                              ? { ...p, show: !!value }
                              : p
                          ))
                        }
                      >
                        {column.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {hasValues ?
            values!.map((row: any, index: number) => (
              <TableRow
                key={props.keyExtractor(row, index)}
                data-state={row.selected === true ? "selected" : ""}
                className="group/item"
              >
                {props.checkable && (
                  <TableCell className="pl-4">
                    <Checkbox
                      checked={row.selected}
                      onCheckedChange={(value) => row['selected'] = true}
                      aria-label="Selecionar registro"
                    />
                  </TableCell>
                )}
                {visibleColumns.map((col: Column, indexCol: number) => (
                  <TableCell key={`${props.keyExtractor(row, index)}-${indexCol}`} className={cn("h-12 px-4", indexCol === 0 ? "font-medium" : "text-foreground/80", isNumeric(col) && "text-right tabular-nums")}>
                    {cellValue(col, row)}
                  </TableCell>
                ))}
                {props.actions && (
                  <TableCell className="px-4">
                    <div className="flex justify-end gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover/item:opacity-100">
                      {props.actions(row)}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            )) :
            props.loading ? (
              [1, 2, 3, 4, 5].map(item => (
                <TableRow key={item} className="hover:bg-transparent">
                  {props.checkable && <TableCell className="pl-4"><Skeleton className="h-4 w-4" /></TableCell>}
                  {visibleColumns.map((col: Column) => (
                    <TableCell key={col.field} className="h-12 px-4">
                      <Skeleton className="h-4 w-full max-w-[10rem]" />
                    </TableCell>
                  ))}
                  {props.actions && <TableCell className="px-4" />}
                </TableRow>
              ))
            ) : emptyState
          }
        </TableBody>
      </Table>
    </div>
    <ul className="divide-y sm:hidden">
      {hasValues ?
        values!.map((row: any, index: number) => (
          <li key={props.keyExtractor(row, index)} className="p-4">
            {/* Primeira coluna como título do cartão, as demais como rótulo/valor */}
            <p className="mb-1.5 break-words font-medium">{mobileColumns[0] && cellValue(mobileColumns[0], row)}</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              {mobileColumns.slice(1).map((col: Column, indexCol: number) => (
                <div key={indexCol} className="contents">
                  <dt className="text-muted-foreground">{col.label}</dt>
                  <dd className="min-w-0 break-words">{cellValue(col, row)}</dd>
                </div>
              ))}
            </dl>
            {/* Ações numa linha abaixo dos dados, para não espremer o conteúdo */}
            {props.actions && <div className="-mb-1 mt-2 flex flex-wrap justify-end gap-1 border-t pt-2">{props.actions(row)}</div>}

          </li>
        )) :
        props.loading ? (
          [1, 2].map(item => (
            <li key={item} className="space-y-2 p-4">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/4" />
            </li>
          ))
        ) : (
          <li className="p-8 text-center text-sm text-muted-foreground">Nenhum registro encontrado.</li>
        )}
    </ul>
    </>
  )
}

type PaginationProps = {
  pagination?: PaginationData;
}

export const Pagination = (props: PaginationProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (!props.pagination) return <></>;
  
  const {totalPages} = props.pagination;
  let number = props.pagination.number+1;

  if (!props.pagination.totalElements) return <></>;

  const numberInicial = (number - 2) >= 1 ? (number - 2) : 1;
  const numberFinal = (number + 3) < totalPages ? (number + 3) : totalPages;
  const onChange = (page: number) => {
    const allParams: string[] =
      ((location && location.search.substr(1).split('&')) || [])
        .filter(param => !param.startsWith('page'));

    allParams.push(`page=${page}`);
    
    navigate({search: allParams.join('&')});
  }

  const inicio = props.pagination.number * props.pagination.size + 1;
  const fim = inicio + props.pagination.numberOfElements - 1;

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t px-4 py-3 sm:flex-row">
      <p className="text-sm text-muted-foreground">
        {props.pagination.numberOfElements > 0 && <>Mostrando <span className="tabular-nums text-foreground">{inicio}–{fim}</span>{props.pagination.totalElements !== undefined && <> de <span className="tabular-nums text-foreground">{props.pagination.totalElements}</span></>}</>}
      </p>
      {totalPages > 1 && (
    <PaginationUI className="mx-0 w-auto">
      <PaginationContent>

        {number > 1 && (
          <PaginationItem>
            <PaginationPrevious className="h-8 cursor-pointer" onClick={() => onChange(number-1)}  />
          </PaginationItem>
        )}
        
        {numberInicial > 1 && (
          <>
          <PaginationItem>
            <PaginationLink className="h-8 w-8 cursor-pointer" onClick={() => onChange(1)}>1</PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
          </>
        )}

        {range(numberInicial, (numberFinal+1)).map((value: any) => (
          <PaginationItem key={value}>
            <PaginationLink className="h-8 w-8 cursor-pointer tabular-nums" isActive={number===value} onClick={() => onChange(value)}>{value}</PaginationLink>
          </PaginationItem>
        ))}

        {numberFinal < (totalPages) && (
          <>
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
          <PaginationItem>
            <PaginationLink className="h-8 w-8 cursor-pointer" onClick={() => onChange(totalPages)}>{totalPages}</PaginationLink>
          </PaginationItem>
          </>
        )}

        {number < totalPages && (
          <PaginationItem>
            <PaginationNext className="h-8 cursor-pointer" onClick={() => onChange(number+1)} />
          </PaginationItem>
        )}
      </PaginationContent>
    </PaginationUI>
      )}
    </div>
  )
}
